import React from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Clock,
    DollarSign,
    Award,
    Calendar,
    Users,
    MapPin,
    Lock,
    Bell,
    BarChart,
    FileText,
    Briefcase,
    Book
} from 'lucide-react';

// 引入組件
import FeatureSection from '../components/features/FeatureSection';
import FeatureShowcase from '../components/features/FeatureShowcase';
import ProcessFlow from '../components/features/ProcessFlow';
import ModuleComparisonTable from '../components/features/ModuleComparisonTable';
import Testimonial from '../components/common/Testimonial';
import SVGDiagram from '../components/features/SVGDiagram';

const FeaturesPage = () => {
    const [headingRef, headingInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // 出勤管理流程步驟
    const attendanceProcess = [
        {
            title: '員工打卡',
            description: '透過手機GPS定位進行上下班打卡',
            icon: <MapPin size={24} />,
            color: 'bg-blue-100 text-blue-600'
        },
        {
            title: '系統驗證',
            description: '自動驗證位置與設備指紋',
            icon: <Lock size={24} />,
            color: 'bg-indigo-100 text-indigo-600'
        },
        {
            title: '記錄生成',
            description: '生成出勤記錄並計算工時',
            icon: <FileText size={24} />,
            color: 'bg-purple-100 text-purple-600'
        },
        {
            title: '異常處理',
            description: '識別遲到、早退等異常情況',
            icon: <Bell size={24} />,
            color: 'bg-red-100 text-red-600'
        },
        {
            title: '數據分析',
            description: '生成出勤統計報表',
            icon: <BarChart size={24} />,
            color: 'bg-green-100 text-green-600'
        }
    ];

    // 薪資計算流程步驟
    const salaryProcess = [
        {
            title: '出勤數據收集',
            description: '整合考勤、加班、請假記錄',
            icon: <Clock size={24} />,
            color: 'bg-blue-100 text-blue-600'
        },
        {
            title: '基本薪資設定',
            description: '設定基本工資、津貼、獎金等',
            icon: <DollarSign size={24} />,
            color: 'bg-green-100 text-green-600'
        },
        {
            title: '績效整合',
            description: '結合績效考核結果',
            icon: <Award size={24} />,
            color: 'bg-orange-100 text-orange-600'
        },
        {
            title: '自動計算',
            description: '自動計算應發薪資與扣除項目',
            icon: <BarChart size={24} />,
            color: 'bg-purple-100 text-purple-600'
        },
        {
            title: '薪資發放',
            description: '生成薪資單並發放',
            icon: <Briefcase size={24} />,
            color: 'bg-red-100 text-red-600'
        }
    ];

    // 模組比較表格數據
    const modulesData = [
        {
            name: '基礎出勤管理模組',
            features: [
                'GPS 簽到簽退',
                '防止代打卡機制',
                '自動計算遲到早退',
                '異常打卡警示',
                '補簽管理'
            ],
            standardPlan: true,
            advancedPlan: true,
            proPlan: true
        },
        {
            name: '薪資與假勤管理模組',
            features: [
                '請假申請與審核',
                '假別管理設定',
                '自動薪資計算',
                '薪資結構管理',
                '離職結算'
            ],
            standardPlan: false,
            advancedPlan: true,
            proPlan: true
        },
        {
            name: '績效與考核管理模組',
            features: [
                'KPI 設定',
                '績效評估流程',
                '與薪資獎金結合',
                '績效報表分析',
                '目標追蹤'
            ],
            standardPlan: false,
            advancedPlan: false,
            proPlan: true
        },
        {
            name: '排班與工時管理模組',
            features: [
                '班別設定',
                '智能排班系統',
                '員工班表查詢',
                '工時統計',
                '薪資整合'
            ],
            standardPlan: true,
            advancedPlan: true,
            proPlan: true
        },
        {
            name: '會議與行事曆管理模組',
            features: [
                '全局行事曆',
                '會議安排',
                '提醒事項',
                '共享提醒',
                '行事曆同步'
            ],
            standardPlan: false,
            advancedPlan: false,
            proPlan: true
        },
        {
            name: '培訓與進修管理模組',
            features: [
                '培訓紀錄',
                '課程管理',
                '證書管理',
                '與績效結合',
                '進修追蹤'
            ],
            standardPlan: false,
            advancedPlan: false,
            proPlan: true
        },
        {
            name: '報表與數據分析模組',
            features: [
                '出勤報表',
                '請假統計',
                '績效分析',
                'Excel/PDF 導出',
                '數據儀表板'
            ],
            standardPlan: false,
            advancedPlan: true,
            proPlan: true
        },
        {
            name: '通知與提醒模組',
            features: [
                'Email 通知',
                'App 推播',
                '上下班提醒',
                '請假審核通知',
                '會議提醒'
            ],
            standardPlan: false,
            advancedPlan: true,
            proPlan: true
        }
    ];

    // 用戶評價數據
    const testimonials = [
        {
            name: '林經理',
            company: '台北科技有限公司',
            quote: '導入系統後，人資部門工作效率提升了30%，大幅減少薪資計算錯誤。特別是GPS打卡功能，徹底杜絕了虛假打卡的問題。',
            image: 'https://via.placeholder.com/80'
        },
        {
            name: '張董事長',
            company: '新竹精密製造',
            quote: '智能排班功能為我們節省了大量人力資源成本，員工滿意度也明顯提高。系統的數據分析幫助我們更好地管理企業人力資源。',
            image: 'https://via.placeholder.com/80'
        },
        {
            name: '王主任',
            company: '高雄服務業集團',
            quote: '系統操作簡單直觀，我們的員工幾乎不需要培訓就能上手。整合的薪資計算功能讓我們的人事成本計算更加精確和透明。',
            image: 'https://via.placeholder.com/80'
        }
    ];

    return (
        <div className="min-h-screen">
            {/* 頁面標題區塊 */}
            <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
                <div className="container mx-auto px-4">
                    <div
                        ref={headingRef}
                        className={`max-w-3xl mx-auto text-center transition-opacity duration-1000 ease-in-out ${headingInView ? 'opacity-100' : 'opacity-0'
                            }`}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">功能特色</h1>
                        <p className="text-xl opacity-90">
                            智能出勤與人事管理系統提供全方位的人力資源管理解決方案，透過多個功能模組滿足企業各種需求。
                        </p>
                    </div>
                </div>
            </section>

            {/* 核心功能概覽 */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">核心功能概覽</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="p-6 bg-gray-50 rounded-lg text-center hover:shadow-md transition-shadow">
                            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                                <Clock size={32} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">出勤管理</h3>
                            <p className="text-gray-600">GPS定位打卡、防偽機制、異常警示</p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-lg text-center hover:shadow-md transition-shadow">
                            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                                <DollarSign size={32} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">薪資管理</h3>
                            <p className="text-gray-600">自動計算薪資、假別管理、結算資遣費</p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-lg text-center hover:shadow-md transition-shadow">
                            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                                <Award size={32} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">績效考核</h3>
                            <p className="text-gray-600">KPI設定、績效評估、與薪資結合</p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-lg text-center hover:shadow-md transition-shadow">
                            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                                <Calendar size={32} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">排班管理</h3>
                            <p className="text-gray-600">智能排班、班表查詢、工時統計</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 出勤管理詳細介紹 */}
            <FeatureSection
                title="基礎出勤管理模組"
                description="透過GPS定位和裝置指紋辨識，確保員工出勤記錄的真實性和準確性。系統能自動計算工時、識別異常情況並發出即時警示。"
                bgColor="bg-gray-50"
                icon={<Clock size={36} className="text-primary-600" />}
            >
                <FeatureShowcase
                    title="GPS定位打卡"
                    description="員工需在指定地點範圍內進行打卡，系統會驗證GPS定位資訊，有效防止員工異地打卡。支援多地點設定，適合有多個辦公地點或外勤需求的企業。"
                    imageUrl="https://via.placeholder.com/600x400"
                    imagePosition="right"
                />

                {/* 可視化的出勤流程圖 */}
                <SVGDiagram
                    type="attendance"
                    title="出勤管理流程圖"
                    description="從員工打卡到數據分析的完整流程"
                />

                <ProcessFlow
                    title="出勤管理流程"
                    description="從員工打卡到數據分析，全程自動化管理"
                    steps={attendanceProcess}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <Lock className="text-primary-600 mb-4" size={28} />
                        <h4 className="text-lg font-semibold mb-2">防止代打卡機制</h4>
                        <p className="text-gray-600">
                            使用FingerprintJS裝置指紋辨識技術，確保每個帳號只能在指定裝置上使用，有效防止代打卡情況。
                        </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <Bell className="text-primary-600 mb-4" size={28} />
                        <h4 className="text-lg font-semibold mb-2">異常打卡警示</h4>
                        <p className="text-gray-600">
                            系統會自動識別遲到、早退或異常位置打卡等情況，並即時通知管理者和相關人員。
                        </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <FileText className="text-primary-600 mb-4" size={28} />
                        <h4 className="text-lg font-semibold mb-2">補簽管理</h4>
                        <p className="text-gray-600">
                            完善的補簽申請與審核流程，員工可提交補簽申請並附上原因，主管可進行線上審核。
                        </p>
                    </div>
                </div>
            </FeatureSection>

            {/* 薪資與假勤管理詳細介紹 */}
            <FeatureSection
                title="薪資與假勤管理模組"
                description="整合出勤數據與公司薪資政策，自動計算員工薪資，減少人為錯誤，提高人事部門工作效率。"
                bgColor="bg-white"
                icon={<DollarSign size={36} className="text-primary-600" />}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-xl font-semibold mb-4">假勤管理功能</h4>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>員工線上請假申請與主管審核</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>多種假別管理與自定義假別</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>自動計算剩餘假期</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>代理人設定與交接</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>請假審核流程自定義</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-xl font-semibold mb-4">薪資管理功能</h4>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>自動計算薪資與加班費</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>自定義薪資結構</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>離職員工資遣費計算</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>薪資單產生與發送</span>
                            </li>
                            <li className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-100 rounded-full mr-3 mt-0.5">
                                    <span className="text-primary-600 text-sm font-bold">✓</span>
                                </div>
                                <span>薪資報表與歷史查詢</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* 可視化的薪資計算流程圖 */}
                <SVGDiagram
                    type="salary"
                    title="薪資核算流程圖"
                    description="自動化薪資計算完整流程"
                />

                <ProcessFlow
                    title="薪資計算流程"
                    description="整合出勤與績效數據，自動計算薪資"
                    steps={salaryProcess}
                />
            </FeatureSection>

            {/* 績效與考核管理詳細介紹 */}
            <FeatureSection
                title="績效與考核管理模組"
                description="建立透明的績效評估體系，連結績效與薪酬，促進員工成長與組織發展。"
                bgColor="bg-gray-50"
                icon={<Award size={36} className="text-primary-600" />}
            >
                <FeatureShowcase
                    title="KPI設定與追蹤"
                    description="系統支援多層級KPI設定，從公司目標到部門目標再到個人目標，形成完整的目標連結。管理者可以實時追蹤目標達成進度，員工也能清楚了解自己的工作目標和進展。"
                    imageUrl="https://via.placeholder.com/600x400"
                    imagePosition="left"
                />

                <div className="bg-white p-6 rounded-lg shadow-sm mt-12">
                    <h4 className="text-xl font-semibold mb-4">績效評估流程</h4>
                    <div className="relative">
                        {/* 時間軸線 */}
                        <div className="absolute h-full w-0.5 bg-primary-200 left-6 top-0"></div>

                        <div className="space-y-8">
                            <div className="relative flex items-start">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-600 rounded-full mr-4 z-10">
                                    <span className="text-white font-bold">1</span>
                                </div>
                                <div className="pt-2">
                                    <h5 className="text-lg font-medium mb-2">目標設定階段</h5>
                                    <p className="text-gray-600">主管與員工共同設定績效目標與KPI指標，明確期望和標準。</p>
                                </div>
                            </div>

                            <div className="relative flex items-start">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-600 rounded-full mr-4 z-10">
                                    <span className="text-white font-bold">2</span>
                                </div>
                                <div className="pt-2">
                                    <h5 className="text-lg font-medium mb-2">中期檢視階段</h5>
                                    <p className="text-gray-600">評估期中進行目標達成進度檢視，必要時調整目標或提供指導。</p>
                                </div>
                            </div>

                            <div className="relative flex items-start">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-600 rounded-full mr-4 z-10">
                                    <span className="text-white font-bold">3</span>
                                </div>
                                <div className="pt-2">
                                    <h5 className="text-lg font-medium mb-2">自評與互評階段</h5>
                                    <p className="text-gray-600">員工進行自我評估，同時可進行同儕互評，提供多角度評價。</p>
                                </div>
                            </div>

                            <div className="relative flex items-start">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-600 rounded-full mr-4 z-10">
                                    <span className="text-white font-bold">4</span>
                                </div>
                                <div className="pt-2">
                                    <h5 className="text-lg font-medium mb-2">主管評核階段</h5>
                                    <p className="text-gray-600">主管根據KPI達成情況與工作表現給予評分與回饋。</p>
                                </div>
                            </div>

                            <div className="relative flex items-start">
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-600 rounded-full mr-4 z-10">
                                    <span className="text-white font-bold">5</span>
                                </div>
                                <div className="pt-2">
                                    <h5 className="text-lg font-medium mb-2">成果應用階段</h5>
                                    <p className="text-gray-600">績效結果與薪資、獎金、職位晉升等掛鉤，並為員工擬定發展計畫。</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </FeatureSection>

            {/* 排班與工時管理詳細介紹 */}
            <FeatureSection
                title="排班與工時管理模組"
                description="智能排班系統自動安排最佳人力配置，提高資源利用效率，降低管理成本。"
                bgColor="bg-white"
                icon={<Calendar size={36} className="text-primary-600" />}
            >
                <FeatureShowcase
                    title="智能排班系統"
                    description="系統根據企業設定的規則與限制條件，如人力需求、員工專長、休假排定、工時限制等，自動生成最優排班方案。管理者可以進一步手動調整，並發布排班結果讓員工查詢。"
                    imageUrl="https://via.placeholder.com/600x400"
                    imagePosition="right"
                />

                {/* 可視化的排班流程圖 */}
                <SVGDiagram
                    type="scheduling"
                    title="智能排班流程圖"
                    description="從設定規則到發布排班的完整流程"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-xl font-semibold mb-4">多班別彈性設定</h4>
                        <p className="text-gray-600 mb-4">
                            支援各種不同班別的設定，包括固定班、輪班、早晚班、特殊節假日班等。企業可根據實際業務需求靈活配置。
                        </p>
                        <ul className="space-y-2">
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>固定班次設定</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>輪班制排班</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>班別時段自定義</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>工時與休息時間設定</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-xl font-semibold mb-4">工時統計與薪資整合</h4>
                        <p className="text-gray-600 mb-4">
                            系統自動統計員工實際工時，包括正常工時、加班工時等，並與薪資計算模組整合，確保準確計算薪資。
                        </p>
                        <ul className="space-y-2">
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>正常工時計算</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>加班時數統計</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>與薪資模組無縫整合</span>
                            </li>
                            <li className="flex items-center">
                                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                <span>工時報表匯出</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </FeatureSection>

            {/* 更多模組 */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4">更多功能模組</h2>
                    <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
                        除了核心模組外，系統還提供其他專業模組，滿足企業全方位的人力資源管理需求。
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-sm">
                            <div className="p-3 bg-primary-100 rounded-full inline-block mb-4">
                                <Bell size={24} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3">通知與提醒模組</h3>
                            <p className="text-gray-600 mb-4">
                                全方位的通知系統，通過Email、App推播等方式及時提醒員工和主管各類重要事件。
                            </p>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>打卡提醒</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>請假審核通知</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>排班變更提醒</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm">
                            <div className="p-3 bg-primary-100 rounded-full inline-block mb-4">
                                <BarChart size={24} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3">報表與數據分析模組</h3>
                            <p className="text-gray-600 mb-4">
                                強大的報表功能，提供多維度數據分析，幫助管理者做出明智決策。
                            </p>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>出勤統計報表</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>薪資報表</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>多格式匯出</span>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm">
                            <div className="p-3 bg-primary-100 rounded-full inline-block mb-4">
                                <Book size={24} className="text-primary-600" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3">培訓與進修管理模組</h3>
                            <p className="text-gray-600 mb-4">
                                完整追蹤員工培訓與進修情況，提升員工能力與企業競爭力。
                            </p>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>培訓課程管理</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>證書追蹤</span>
                                </li>
                                <li className="flex items-center">
                                    <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                                    <span>與績效整合</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 模組比較表 */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4">模組比較</h2>
                    <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
                        各方案所包含的功能模組對比，幫助您選擇最適合企業需求的方案。
                    </p>

                    <ModuleComparisonTable modules={modulesData} />
                </div>
            </section>

            {/* 系統優勢 */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4">系統優勢</h2>
                    <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
                        智能出勤與人事管理系統相比傳統人事系統具有多方面優勢。
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <MapPin size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">GPS定位打卡</h3>
                                <p className="text-gray-600">
                                    傳統系統：固定打卡機，容易代打卡<br />
                                    本系統：GPS定位+裝置指紋，確保真實打卡
                                </p>
                            </div>
                        </div>

                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <DollarSign size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">薪資計算</h3>
                                <p className="text-gray-600">
                                    傳統系統：手動計算，易出錯<br />
                                    本系統：自動整合考勤數據，準確計算薪資
                                </p>
                            </div>
                        </div>

                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <Calendar size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">排班管理</h3>
                                <p className="text-gray-600">
                                    傳統系統：人工排班，耗時費力<br />
                                    本系統：智能排班算法，自動優化人力配置
                                </p>
                            </div>
                        </div>

                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <BarChart size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">數據分析</h3>
                                <p className="text-gray-600">
                                    傳統系統：有限的報表功能<br />
                                    本系統：多維度數據分析，支援決策制定
                                </p>
                            </div>
                        </div>

                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <Users size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">員工自助服務</h3>
                                <p className="text-gray-600">
                                    傳統系統：依賴人事部門處理<br />
                                    本系統：員工可自行查詢資訊、提交申請
                                </p>
                            </div>
                        </div>

                        <div className="flex">
                            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                                <Bell size={24} className="text-primary-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-3">即時通知</h3>
                                <p className="text-gray-600">
                                    傳統系統：依賴人工通知<br />
                                    本系統：多渠道即時通知，不遺漏重要訊息
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 客戶見證 */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4">客戶見證</h2>
                    <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
                        來自各行業的企業分享使用系統後的實際效益。
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <Testimonial
                                key={index}
                                name={testimonial.name}
                                company={testimonial.company}
                                quote={testimonial.quote}
                                image={testimonial.image}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* 呼籲行動區塊 */}
            <section className="bg-primary-600 text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-4">立即體驗智能出勤與人事管理系統</h2>
                    <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                        提供 14 天免費試用期，無需信用卡，體驗如何透過智能系統提升企業管理效率。
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <a
                            href="/contact"
                            className="px-8 py-3 bg-white text-primary-700 font-medium rounded-lg hover:bg-gray-100"
                        >
                            免費試用
                        </a>
                        <a
                            href="/pricing"
                            className="px-8 py-3 border border-white text-white font-medium rounded-lg hover:bg-primary-700"
                        >
                            查看方案價格
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FeaturesPage;